// ============================================================
// SIGHT - what the opposition could see, as a QUESTION for the GM
// (plan map-display, H; the rulings of 2026-10-07).
//
// A watcher sees as far as the TARGET's light allows: 6 m to someone in the
// dark, 11 m to someone in the light. Dark Sight (a trait on the sheet)
// sees the dark as if lit - which is why a long wedge in the dark is the
// tell. A party member within reach and in clear sight is a SIGHTING: the
// display never detects alone, it proposes a call (Concealment against the
// watcher's Awareness, the watcher at -20 in the dark) and the GM decides.
// One question per CHARACTER newly in anyone's sight (2026-10-28); one still seen is not asked again.
//
// The sweep the display draws is the rung's tempo: quicker as the place
// wakes. It is the look of watching, not a cone the rules read: the rules
// read reach and line of sight.
//
// Pure. Zero React dependency.
// ============================================================
import type { Rung, StealthState } from './ladder';
import { watchers } from './opposition';
import type { Combatant } from '../roster/types';
import { traitIdsOf } from '../battlefield/traitRules';
import type { TestCall } from '../narrative/callForTest';
import type { GroundPoint } from '../battlefield/facing';

/** How far a watcher sees, in metres, to a target in the dark and in the light. */
const SIGHT_DARK_M = 6;
export const SIGHT_LIT_M = 11;
/**
 * How much further a character already seen stays seen, in metres (2026-10-28): without it, a character standing at
 * the edge of a watcher's reach was asked about again each time they stepped across it.
 */
const SIGHT_HOLD_M = 1;
/** The watcher's Awareness in the dark (the Darkness modifier, rulebook). */
const DARK_PENALTY = -20;

export interface Sighting {
  watcherId: string;
  targetId: string;
  /** Metres, on the ground. */
  distance: number;
  /** Whether the target stands in the light. */
  lit: boolean;
  /** Whether the watcher sees the dark as lit: its Awareness is not made harder there. */
  darkSight: boolean;
}

/** Whether this one sees the dark as if lit: the Dark Sight trait on its sheet. */
const hasDarkSight = (c: { sheet?: unknown } | null | undefined): boolean =>
  traitIdsOf((c?.sheet ?? {}) as Parameters<typeof traitIdsOf>[0]).includes('dark_sight');

/** Who is watching, with their Dark Sight: what the display's wedges are drawn for. */
export const watchingOf = (opposition: readonly Combatant[], state: StealthState) =>
  watchers(opposition, state).map((c) => ({ id: c.id, darkSight: hasDarkSight(c) }));

/** How far this watcher sees a target in this light. */
export const sightReach = (targetLit: boolean, darkSight: boolean): number =>
  targetLit || darkSight ? SIGHT_LIT_M : SIGHT_DARK_M;

/**
 * Every (watcher, party member) pair in sight: within the reach the member's light allows, and with nothing
 * blocking the line between them (`blocked`, the map's own line of sight). A member in `held` (seen already) is
 * seen a metre further (SIGHT_HOLD_M).
 */
export function sightings(
  watchers: readonly { id: string; at: GroundPoint; darkSight: boolean }[],
  party: readonly { id: string; at: GroundPoint }[],
  litAt: (at: GroundPoint) => boolean,
  blocked: (watcherId: string, targetId: string) => boolean,
  held: ReadonlySet<string> = new Set(),
): Sighting[] {
  // Each member's light once, not once per watcher.
  const lit = new Map(party.map((p) => [p.id, litAt(p.at)]));
  return watchers.flatMap((w) =>
    party.flatMap((p) => {
      const inLight = lit.get(p.id)!;
      const distance = Math.hypot(p.at.x - w.at.x, p.at.z - w.at.z);
      const reach = sightReach(inLight, w.darkSight) + (held.has(p.id) ? SIGHT_HOLD_M : 0);
      if (distance > reach || blocked(w.id, p.id)) return [];
      const metres = Math.round(distance * 10) / 10;
      return [{ watcherId: w.id, targetId: p.id, distance: metres, lit: inLight, darkSight: w.darkSight }];
    }),
  );
}

/** One character in sight, and every watcher who could see them, nearest first: ONE question for the GM. */
export interface SeenTarget {
  targetId: string;
  watchers: Sighting[];
}

/** The sightings by the character seen, in the order first met; each one's watchers nearest first. */
export function byTarget(now: readonly Sighting[]): SeenTarget[] {
  const groups = new Map<string, Sighting[]>();
  for (const s of now) groups.set(s.targetId, [...(groups.get(s.targetId) ?? []), s]);
  return [...groups].map(([targetId, seen]) => ({
    targetId,
    watchers: [...seen].sort((a, b) => a.distance - b.distance),
  }));
}

/**
 * The characters newly in sight - not in `before` (ids of those already seen): the questions worth asking
 * (2026-10-28, the ruling: one question per character, watchers listed, at most one rung).
 */
export const freshTargets = (before: ReadonlySet<string>, now: readonly Sighting[]): SeenTarget[] =>
  byTarget(now).filter((t) => !before.has(t.targetId));

/** Seconds per turn of the watchers' sweep at each rung. */
const SWEEP_S: Record<Rung, number> = { unaware: 12, uneasy: 9, searching: 6, alarm: 3 };

export const sweepPeriod = (rung: Rung): number => SWEEP_S[rung];

/** The call a sighting proposes: the member's Concealment against the watcher's Awareness. */
export function sightingCall(s: Sighting): TestCall {
  return {
    who: [s.targetId],
    subject: { kind: 'skill', id: 'concealment' },
    difficulty: 0,
    against: {
      who: s.watcherId,
      subject: { kind: 'skill', id: 'awareness' },
      difficulty: s.lit || s.darkSight ? 0 : DARK_PENALTY,
    },
    secret: false,
  };
}
